import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Nav from "../components/Nav";
import SEO, { buildBreadcrumb } from "../components/SEO";
import api from "../lib/api";

const LEGAL_DOCUMENTS = {
  "artist-agreement": {
    title: "Artist Agreement",
    description: "BookTalent's Artist Agreement supplied on 8 September 2026.",
    file: "/legal/artist-agreement-2026-09-08.html",
    original: "/legal/artist-agreement-2026-09-08.docx",
  },
};

const CMS_LEGAL_DOCUMENTS = {
  privacy: {
    title: "Privacy Policy",
    original: "/legal/privacy-policy-2026-09-08.docx",
  },
  terms: {
    title: "Terms & Conditions",
    original: "/legal/terms-and-conditions-2026-09-08.docx",
  },
};

function prepareLegalContent(markup) {
  const document = new DOMParser().parseFromString(markup, "text/html");
  const body = document.body;
  const sections = [];

  body.querySelectorAll('a[href="https://book"]').forEach((link) => {
    link.href = "https://booktalent.in/";
    link.textContent = "https://booktalent.in/";
    const next = link.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE) {
      next.textContent = next.textContent.replace(/^talent\.in\/?/, "");
    }
  });

  body.querySelectorAll("p").forEach((paragraph) => {
    const text = paragraph.textContent.trim();
    const letters = text.replace(/[^A-Za-z]/g, "");
    if (letters.length < 3 || text !== text.toLocaleUpperCase()) return;
    if (text === "TERMS AND CONDITIONS") {
      paragraph.remove();
      return;
    }
    const heading = document.createElement(/^PART [A-D]\b/.test(text) ? "h2" : "h3");
    heading.textContent = text;
    paragraph.replaceWith(heading);
  });

  body.querySelectorAll("h2, h3").forEach((heading, index) => {
    const headingText = heading.textContent.trim();
    const id = `legal-section-${index + 1}`;
    heading.id = id;
    if (
      heading.tagName === "H2" ||
      headingText === "DEFINITIONS AND INTERPRETATION"
    ) {
      sections.push({ id, title: headingText });
    }
  });

  return { html: body.innerHTML, sections };
}

/**
 * /page/:slug — Renders any CMS page created from the Admin Panel.
 * SEO: dynamic title/desc/keywords/OG + WebPage & BreadcrumbList JSON-LD.
 */
export default function CmsPage() {
  const { slug } = useParams();
  const isLegalDocument = Object.prototype.hasOwnProperty.call(LEGAL_DOCUMENTS, slug);
  const legalDocument = isLegalDocument ? LEGAL_DOCUMENTS[slug] : null;
  const cmsLegalDocument = CMS_LEGAL_DOCUMENTS[slug] || null;
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [legalHtml, setLegalHtml] = useState("");
  const [legalSections, setLegalSections] = useState([]);
  const [legalLoading, setLegalLoading] = useState(false);
  const [legalError, setLegalError] = useState(false);

  useEffect(() => {
    setPage(null);
    setError(null);
    if (isLegalDocument) return;
    api.get(`/cms/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Not Found" : "Failed to load"));
  }, [isLegalDocument, slug]);

  const safeHtml = useMemo(() => DOMPurify.sanitize(page?.body_html || ""), [page?.body_html]);
  const safeLegalHtml = useMemo(() => DOMPurify.sanitize(legalHtml), [legalHtml]);
  const cmsLegalContent = useMemo(
    () => (cmsLegalDocument && page?.body_html ? prepareLegalContent(page.body_html) : { html: "", sections: [] }),
    [cmsLegalDocument, page?.body_html],
  );
  const safeCmsLegalHtml = useMemo(
    () => DOMPurify.sanitize(cmsLegalContent.html),
    [cmsLegalContent.html],
  );

  useEffect(() => {
    if (!legalDocument) {
      setLegalHtml("");
      setLegalSections([]);
      setLegalError(false);
      return undefined;
    }

    let active = true;
    setLegalLoading(true);
    setLegalError(false);
    setLegalHtml("");
    setLegalSections([]);

    const loadLegalContent = async () => {
      const response = await fetch(legalDocument.file);
      if (!response.ok) throw new Error(`Legal document request failed: ${response.status}`);
      const markup = await response.text();
      const content = prepareLegalContent(markup);
      if (!content.html.trim()) throw new Error("Legal document body is empty");
      if (active) {
        setLegalHtml(content.html);
        setLegalSections(content.sections);
      }
    };

    loadLegalContent()
      .catch(() => {
        if (active) setLegalError(true);
      })
      .finally(() => {
        if (active) setLegalLoading(false);
      });

    return () => { active = false; };
  }, [legalDocument, slug]);

  if (legalDocument) {
    return (
      <div data-testid={`legal-page-${slug}`}>
        <SEO title={legalDocument.title} description={legalDocument.description} path={`/page/${slug}`} />
        <Nav />
        <section className="section container legal-page">
          <header className="legal-page-header">
            <div>
              <h1 className="font-serif">{legalDocument.title}</h1>
            </div>
            <a className="btn btn-gold btn-sm" href={legalDocument.original} download>
              Download original DOCX
            </a>
          </header>
          <article
            className="legal-content"
            data-testid="legal-document-content"
            aria-busy={legalLoading}
          >
            {legalLoading && <div className="skeleton legal-loading" aria-label="Loading legal document" />}
            {legalError && (
              <p className="legal-error" role="alert">
                This document could not be loaded. Please refresh the page or download the original document.
              </p>
            )}
            {!legalLoading && !legalError && (
              <>
                {legalSections.length > 0 && (
                  <nav className="legal-contents" aria-label="Terms sections" data-testid="legal-contents">
                    <div className="legal-contents-title">On this page</div>
                    <div className="legal-contents-links">
                      {legalSections.map((section) => (
                        <a key={section.id} href={`#${section.id}`}>{section.title}</a>
                      ))}
                    </div>
                  </nav>
                )}
                <div className="cms-body" dangerouslySetInnerHTML={{ __html: safeLegalHtml }} />
              </>
            )}
          </article>
        </section>
      </div>
    );
  }

  if (error) {
    const isNotFound = error === "Not Found";
    return (
      <div>
        <SEO
          title={isNotFound ? "Page Not Found" : cmsLegalDocument?.title || "Page unavailable"}
          noindex
          path={`/page/${slug}`}
        />
        <Nav />
        <section className="section container cms-error-page" role="alert">
          <h1>{isNotFound ? "Page Not Found" : "This page could not be loaded"}</h1>
          <p className="text-muted">
            {isNotFound
              ? "The page you're looking for doesn't exist or has been unpublished."
              : "Please refresh the page or try again later."}
          </p>
          <Link to="/" className="btn btn-gold" data-testid="cms-back-home">Back to Home</Link>
        </section>
      </div>
    );
  }

  if (cmsLegalDocument) {
    const title = page?.seo_title || page?.title || cmsLegalDocument.title;
    const description = page?.meta_description || page?.title || cmsLegalDocument.title;
    return (
      <div data-testid={`cms-legal-page-${slug}`}>
        <SEO
          title={title}
          description={description}
          keywords={page?.seo_keywords || ""}
          image={page?.og_image || undefined}
          canonical={page?.canonical || undefined}
          path={`/page/${slug}`}
          jsonLd={[
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: title,
              description,
              url: `https://booktalent.in/page/${slug}`,
            },
            buildBreadcrumb([
              { name: "Home", url: "/" },
              { name: title, url: `/page/${slug}` },
            ]),
          ]}
        />
        <Nav />
        <section className="section container legal-page">
          <header className="legal-page-header">
            <div>
              <h1 className="font-serif">{page?.title || cmsLegalDocument.title}</h1>
              {page?.updated_at && (
                <div className="text-muted fs-12 mt-8" data-testid="cms-updated">
                  Last updated: {String(page.updated_at).slice(0, 10)}
                </div>
              )}
            </div>
            <a className="btn btn-gold btn-sm" href={cmsLegalDocument.original} download>
              Download original DOCX
            </a>
          </header>
          <article className="legal-content" aria-busy={!page}>
            {!page ? (
              <div className="skeleton legal-loading" aria-label="Loading CMS document" />
            ) : (
              <>
                {cmsLegalContent.sections.length > 0 && (
                  <nav className="legal-contents" aria-label="Document sections" data-testid="legal-contents">
                    <div className="legal-contents-title">On this page</div>
                    <div className="legal-contents-links">
                      {cmsLegalContent.sections.map((section) => (
                        <a key={section.id} href={`#${section.id}`}>{section.title}</a>
                      ))}
                    </div>
                  </nav>
                )}
                <div
                  className="cms-body"
                  data-testid="cms-body"
                  dangerouslySetInnerHTML={{ __html: safeCmsLegalHtml }}
                />
              </>
            )}
          </article>
        </section>
      </div>
    );
  }

  const title = page?.seo_title || page?.title || slug;
  const desc = page?.meta_description || page?.title || "";
  let extraLd = null;
  if (page?.schema_json) {
    try { extraLd = JSON.parse(page.schema_json); } catch (_) { extraLd = null; }
  }

  return (
    <div data-testid={`cms-page-${slug}`}>
      <SEO
        title={title}
        description={desc}
        keywords={page?.seo_keywords || ""}
        image={page?.og_image || undefined}
        canonical={page?.canonical || undefined}
        path={`/page/${slug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            description: desc,
            url: `https://booktalent.in/page/${slug}`,
          },
          buildBreadcrumb([
            { name: "Home", url: "/" },
            { name: title, url: `/page/${slug}` },
          ]),
          ...(extraLd ? [extraLd] : []),
        ]}
      />
      <Nav />
      <section className="section container" style={{ paddingTop: page?.hero_image || page?.hero_title ? 0 : "var(--space-16)", minHeight: "60vh" }}>
        <div className="cms-content">
          {!page ? (
            <div className="skeleton" style={{ height: 400 }} />
          ) : (
            <>
              {(page.hero_image || page.hero_title) && (
                <div
                  className="page-hero"
                  data-testid="cms-hero"
                  style={{
                    backgroundImage: page.hero_image ? `linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(${page.hero_image})` : undefined,
                  }}
                >
                  <div className="page-hero-inner">
                    <h1 data-testid="cms-hero-title">{page.hero_title || page.title}</h1>
                    {page.hero_subtitle && <p data-testid="cms-hero-subtitle">{page.hero_subtitle}</p>}
                    {page.hero_cta_url && (
                      <a href={page.hero_cta_url} className="btn btn-gold" data-testid="cms-hero-cta">
                        {page.hero_cta_label || "Learn more"} →
                      </a>
                    )}
                  </div>
                </div>
              )}
              {!(page.hero_image || page.hero_title) && (
                <h1 style={{ fontSize: 42, marginBottom: "var(--space-3)" }} data-testid="cms-title">{page.title}</h1>
              )}
              {page.updated_at && (
                <div className="text-muted fs-12 mb-24" data-testid="cms-updated">
                  Last updated: {String(page.updated_at).slice(0, 10)}
                </div>
              )}
              <div
                className="cms-body"
                data-testid="cms-body"
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </>
          )}
        </div>
      </section>
</div>
  );
}
